import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { ArrowRightIcon, AlertCircleIcon } from 'lucide-react';
export function Apply() {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  const [isTimeTravel, setIsTimeTravel] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [buttonPosition, setButtonPosition] = useState({
    x: 0,
    y: 0
  });
  const handleApplyClick = (e: React.MouseEvent) => {
    e.preventDefault();
    // Get button position for centered animation
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setButtonPosition({
        x: rect.left + rect.width / 2,
        y: rect.top + rect.height / 2
      });
    }
    setIsTimeTravel(true);
    setTimeout(() => {
      window.open('https://forms.gle/ibuLiBnpwFqdeVNy7', '_blank');
      setTimeout(() => {
        setIsTimeTravel(false);
      }, 1000);
    }, 4000);
  };
  return <section id="apply" ref={ref} className="w-full py-32 px-8 bg-slate-900 relative overflow-hidden" data-id="element-71">
      <div className="max-w-4xl mx-auto relative z-10" data-id="element-72">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={inView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6
      }} className="text-center mb-16" data-id="element-73">
          <div className="inline-block px-4 py-2 bg-white/10 rounded-full mb-6" data-id="element-74">
            <span className="text-sm font-semibold text-white" data-id="element-75">JOIN US</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6" data-id="element-76">
            Apply to Pericles Capital
          </h2>
          <p className="text-xl text-slate-300 max-w-2xl mx-auto mb-8" data-id="element-77">
            We welcome students from mathematics, computer science, data
            science, economics, and the humanities who are{' '}
            <span className="text-white font-semibold" data-id="element-78">
              serious about analytical thinking,
            </span>{' '}
            <span className="text-white font-semibold" data-id="element-79">collaboration,</span> and{' '}
            <span className="text-white font-semibold" data-id="element-80">
              long-term learning.
            </span>
          </p>
          <motion.button ref={buttonRef} onClick={handleApplyClick} whileHover={{
          scale: 1.05
        }} whileTap={{
          scale: 0.95
        }} disabled={isTimeTravel} className="inline-flex items-center gap-3 px-10 py-5 bg-white text-slate-900 rounded-lg font-bold text-xl hover:bg-slate-100 transition-all shadow-xl disabled:opacity-70" data-id="element-81">
            Submit Application
            <ArrowRightIcon className="w-6 h-6" data-id="element-82" />
          </motion.button>
        </motion.div>
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={inView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6,
        delay: 0.2
      }} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8" data-id="element-83">
          <div className="flex items-start gap-4" data-id="element-84">
            <AlertCircleIcon className="w-6 h-6 text-slate-400 flex-shrink-0 mt-1" data-id="element-85" />
            <div className="space-y-3" data-id="element-86">
              <h3 className="text-xl font-bold text-white" data-id="element-87">
                Important Disclaimer
              </h3>
              <div className="space-y-2 text-slate-300" data-id="element-88">
                <p data-id="element-89">
                  • Pericles Capital is a student research organization for
                  educational purposes
                </p>
                <p data-id="element-90">• Nothing on this site constitutes investment advice</p>
                <p data-id="element-91">• We do not manage external capital</p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
      {/* Epic Time Travel Effect - Centered on button with dramatic fade to black */}
      <AnimatePresence data-id="element-92">
        {isTimeTravel && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-40" data-id="element-93">
            {/* Gradual fade to pitch black */}
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 2,
          ease: [0.22, 1, 0.36, 1]
        }} className="absolute inset-0 bg-black" data-id="element-94" />
            {/* Expanding energy rings centered on button */}
            {[0, 1, 2, 3, 4, 5, 6, 7, 8].map(index => <motion.div key={`ring-${index}`} className="absolute border-4 rounded-full z-50" style={{
          left: buttonPosition.x,
          top: buttonPosition.y,
          width: '120px',
          height: '120px',
          transform: 'translate(-50%, -50%)',
          borderColor: `rgba(59, 130, 246, ${1 - index * 0.1})`,
          boxShadow: `0 0 40px rgba(59, 130, 246, ${0.8 - index * 0.08})`
        }} initial={{
          scale: 0,
          opacity: 0
        }} animate={{
          scale: [0, 25],
          opacity: [0.9, 0.6, 0]
        }} transition={{
          duration: 3.5,
          delay: index * 0.2,
          ease: [0.22, 1, 0.36, 1]
        }} data-id="element-95" />)}
            {/* Rotating energy beams */}
            {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map(index => <motion.div key={`beam-${index}`} className="absolute bg-gradient-to-r from-transparent via-blue-400 to-transparent z-50" style={{
          left: buttonPosition.x,
          top: buttonPosition.y,
          width: '4px',
          height: '150vh',
          transformOrigin: 'center center',
          transform: 'translate(-50%, -50%)',
          filter: 'blur(2px)'
        }} initial={{
          rotate: index * 30,
          opacity: 0,
          scaleY: 0
        }} animate={{
          rotate: [index * 30, index * 30 + 720],
          opacity: [0, 0.8, 0.5, 0],
          scaleY: [0, 1, 1, 0.5]
        }} transition={{
          duration: 4,
          ease: [0.22, 1, 0.36, 1]
        }} data-id="element-96" />)}
            {/* Central energy core */}
            <motion.div className="absolute rounded-full z-50" style={{
          left: buttonPosition.x,
          top: buttonPosition.y,
          width: '80px',
          height: '80px',
          transform: 'translate(-50%, -50%)',
          background: 'radial-gradient(circle, rgba(59, 130, 246, 1) 0%, rgba(29, 78, 216, 0.8) 50%, transparent 100%)',
          boxShadow: '0 0 100px rgba(59, 130, 246, 1), 0 0 200px rgba(59, 130, 246, 0.6)'
        }} initial={{
          scale: 0,
          opacity: 0
        }} animate={{
          scale: [0, 1.5, 3, 5, 8],
          opacity: [0, 1, 0.8, 0.4, 0]
        }} transition={{
          duration: 4,
          ease: [0.22, 1, 0.36, 1]
        }} data-id="element-97" />
            {/* Explosive particle burst */}
            {Array.from({
          length: 60
        }).map((_, index) => {
          const angle = index / 60 * Math.PI * 2;
          const distance = 400 + Math.random() * 400;
          const size = 3 + Math.random() * 4;
          return <motion.div key={`particle-${index}`} className="absolute rounded-full z-50" style={{
            left: buttonPosition.x,
            top: buttonPosition.y,
            width: `${size}px`,
            height: `${size}px`,
            background: `radial-gradient(circle, rgba(59, 130, 246, 1), rgba(96, 165, 250, 0.6))`,
            boxShadow: '0 0 10px rgba(59, 130, 246, 0.8)'
          }} initial={{
            x: 0,
            y: 0,
            opacity: 0,
            scale: 0
          }} animate={{
            x: Math.cos(angle) * distance,
            y: Math.sin(angle) * distance,
            opacity: [0, 1, 0.8, 0],
            scale: [0, 1.5, 2, 0]
          }} transition={{
            duration: 3,
            delay: Math.random() * 0.5,
            ease: [0.22, 1, 0.36, 1]
          }} data-id="element-98" />;
        })}
            {/* Spiral energy waves */}
            {[0, 1, 2, 3].map(index => <motion.div key={`spiral-${index}`} className="absolute border-2 border-blue-400 rounded-full z-50" style={{
          left: buttonPosition.x,
          top: buttonPosition.y,
          width: '200px',
          height: '200px',
          transform: 'translate(-50%, -50%)',
          filter: 'blur(1px)'
        }} initial={{
          scale: 0,
          opacity: 0,
          rotate: 0
        }} animate={{
          scale: [0, 15],
          opacity: [0.8, 0.4, 0],
          rotate: [0, 360]
        }} transition={{
          duration: 3,
          delay: index * 0.3,
          ease: [0.22, 1, 0.36, 1]
        }} data-id="element-99" />)}
            {/* Glowing orbs floating outward */}
            {Array.from({
          length: 20
        }).map((_, index) => {
          const angle = index / 20 * Math.PI * 2;
          const distance = 200 + Math.random() * 300;
          return <motion.div key={`orb-${index}`} className="absolute rounded-full z-50" style={{
            left: buttonPosition.x,
            top: buttonPosition.y,
            width: '16px',
            height: '16px',
            background: 'radial-gradient(circle, rgba(59, 130, 246, 1), rgba(96, 165, 250, 0.4))',
            boxShadow: '0 0 30px rgba(59, 130, 246, 1)',
            filter: 'blur(1px)'
          }} initial={{
            x: 0,
            y: 0,
            opacity: 0,
            scale: 0
          }} animate={{
            x: Math.cos(angle) * distance,
            y: Math.sin(angle) * distance,
            opacity: [0, 1, 0.6, 0],
            scale: [0, 1.8, 1.5, 0]
          }} transition={{
            duration: 3.5,
            delay: index * 0.1,
            ease: [0.22, 1, 0.36, 1]
          }} data-id="element-100" />;
        })}
            {/* Final flash before opening */}
            <motion.div className="absolute inset-0 bg-white z-50" initial={{
          opacity: 0
        }} animate={{
          opacity: [0, 0, 0, 0, 0.3, 0]
        }} transition={{
          duration: 4,
          times: [0, 0.7, 0.85, 0.9, 0.95, 1]
        }} data-id="element-101" />
          </motion.div>}
      </AnimatePresence>
    </section>;
}